import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { EditorLayout } from './layouts/EditorLayout';
import { PlayerLayout } from './layouts/PlayerLayout';
import { RouteFallback } from './components/common';
import { RequireSso } from './auth';

// EAA-1670: code-split every route so the initial bundle only ships the
// shell + the landing route's chunk; heavy editor/player/embed/h5p pages
// (each pulling in content-type renderers) load on demand behind Suspense.
// Layouts stay eager — they're lightweight shells reused across routes, and
// each provides its own inner Suspense so intra-layout navigation keeps the
// chrome mounted while the next page chunk downloads.
const EditorPage = lazy(() => import('./pages/EditorPage'));
const ContentListPage = lazy(() => import('./pages/ContentListPage'));
const ContentEditPage = lazy(() => import('./pages/ContentEditPage'));
const ContentPlayPage = lazy(() => import('./pages/ContentPlayPage'));
// EAA-755: import the page MODULE, never the folder barrel. A barrel re-exports
// every sibling page, so `import('./pages/embed')` pulled all four embed pages and
// their whole content-type graph into one chunk — the four lazy() calls below
// resolved to the same chunk and code-splitting bought nothing. A HAR of the dev
// server showed all five embed modules loading whichever route was opened.
const DemoPage = lazy(() => import('./pages/player/DemoPage'));
const PlayerPage = lazy(() => import('./pages/player/PlayerPage'));
const EmbedEditorPage = lazy(() => import('./pages/embed/EmbedEditorPage'));
const EmbedPlayerPage = lazy(() => import('./pages/embed/EmbedPlayerPage'));
const EmbedMultiPlayerPage = lazy(() => import('./pages/embed/EmbedMultiPlayerPage'));
const EmbedAIPage = lazy(() => import('./pages/embed/EmbedAIPage'));
const H5PEmbedEditorPage = lazy(() => import('./pages/h5p/H5PEmbedEditorPage'));
const H5PEmbedPlayerPage = lazy(() => import('./pages/h5p/H5PEmbedPlayerPage'));
const CallbackPage = lazy(() => import('./pages/Callback/CallbackPage'));

const App: React.FC = () => {
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                {/* EAA-1694: the OIDC redirect target. Deliberately OUTSIDE RequireSso —
                    at this point the session does not exist yet, so gating it would loop. */}
                <Route path="/callback" element={<CallbackPage />} />

                {/* Main editor routes with layout.

                    EAA-1694: SSO gates the AUTHORING routes only. The /play, /embed/* and
                    /h5p/* routes below are opened by a host LMS with a content-session JWT
                    or a public key and never had an interactive user session — gating them
                    would break every existing embed. With VITE_AUTH_MODE=token, RequireSso
                    is a pass-through, so the historical pasted-API-token flow is unchanged. */}
                <Route
                    path="/"
                    element={
                        <RequireSso>
                            <EditorLayout />
                        </RequireSso>
                    }
                >
                    <Route index element={<ContentListPage />} />
                    {/* New content edit route: /edit/new or /edit/:contentId */}
                    <Route path="edit/new" element={<ContentEditPage />} />
                    <Route path="edit/:contentId" element={<ContentEditPage />} />
                    {/* Legacy content type routes */}
                    <Route path="content/:contentType" element={<EditorPage />} />
                    <Route path="content/:contentType/:contentId" element={<EditorPage />} />
                </Route>

                {/* Player routes with player layout */}
                <Route path="/play" element={<PlayerLayout />}>
                    <Route index element={<ContentPlayPage />} />
                    {/* Demo route: /play/demo?type=mcq */}
                    <Route path="demo" element={<ContentPlayPage />} />
                    {/* Legacy demo page */}
                    <Route path="demo-legacy" element={<DemoPage />} />
                    <Route path="content" element={<PlayerPage />} />
                    {/* Content play: /play/:contentId */}
                    <Route path=":contentId" element={<ContentPlayPage />} />
                </Route>

                {/* Embed routes - no layout wrapper for iframe embedding */}
                <Route path="/embed/editor" element={<EmbedEditorPage />} />
                <Route path="/embed/player" element={<EmbedPlayerPage />} />
                <Route path="/embed/multi" element={<EmbedMultiPlayerPage />} />
                <Route path="/embed/ai" element={<EmbedAIPage />} />

                {/* H5P embed routes - no layout wrapper for iframe embedding */}
                <Route path="/h5p/editor" element={<H5PEmbedEditorPage />} />
                <Route path="/h5p/play" element={<H5PEmbedPlayerPage />} />
                <Route path="/h5p/player" element={<H5PEmbedPlayerPage />} />

                {/* Catch-all redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
};

export default App;
